import { FormatRegistry, Type } from "@sinclair/typebox";
import { Value } from "@sinclair/typebox/value";
import { ADE_CADASTRE_MAPPING_VERSION } from "./ade-cadastre-exclusion-policy.js";
export const enrichmentSha256Schema = Type.String({ pattern: "^[0-9a-f]{64}$" });
const sha256Schema = enrichmentSha256Schema;
const rfc3339Pattern = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.\d+)?(?:Z|([+-])(\d{2}):(\d{2}))$/u;
const serializableUrlQueryParameters = new Set(["metadataid"]);
const serializableUrlFlagParameters = new Set([
    "indir_emil",
    "indir_lomb",
    "indir_vene",
    "indir_tosc"
]);
const serializableUrlQueryValuePattern = /^[a-zA-Z0-9._:-]{1,500}$/u;
function isRfc3339Timestamp(value) {
    const match = rfc3339Pattern.exec(value);
    if (!match)
        return false;
    const [, yearText, monthText, dayText, hourText, minuteText, secondText, , offsetHourText, offsetMinuteText] = match;
    const year = Number(yearText);
    const month = Number(monthText);
    const day = Number(dayText);
    const hour = Number(hourText);
    const minute = Number(minuteText);
    const second = Number(secondText);
    const offsetHour = offsetHourText === undefined ? 0 : Number(offsetHourText);
    const offsetMinute = offsetMinuteText === undefined ? 0 : Number(offsetMinuteText);
    const daysInMonth = new Date(Date.UTC(year, month, 0)).getUTCDate();
    return (year >= 1 &&
        month >= 1 &&
        month <= 12 &&
        day >= 1 &&
        day <= daysInMonth &&
        hour <= 23 &&
        minute <= 59 &&
        second <= 59 &&
        offsetHour <= 23 &&
        offsetMinute <= 59 &&
        Number.isFinite(Date.parse(value)));
}
export function isSerializableEnrichmentHttpUrl(value) {
    if (value.length > 2_000 || value.includes("#"))
        return false;
    try {
        const url = new URL(value);
        if ((url.protocol !== "https:" && url.protocol !== "http:") ||
            url.hostname.length === 0 ||
            url.username !== "" ||
            url.password !== "") {
            return false;
        }
        if (!value.includes("?"))
            return true;
        if (url.search === "")
            return false;
        const seenParameters = new Set();
        for (const [rawKey, queryValue] of url.searchParams) {
            const key = rawKey.toLowerCase();
            const allowedValueParameter = serializableUrlQueryParameters.has(key) &&
                serializableUrlQueryValuePattern.test(queryValue);
            const allowedFlagParameter = serializableUrlFlagParameters.has(key) && queryValue === "";
            if (seenParameters.has(key) || (!allowedValueParameter && !allowedFlagParameter)) {
                return false;
            }
            seenParameters.add(key);
        }
        return seenParameters.size > 0;
    }
    catch {
        return false;
    }
}
export function assertSerializableEnrichmentHttpUrl(value, field = "URL") {
    if (typeof value !== "string" || !isSerializableEnrichmentHttpUrl(value)) {
        throw new Error(`${field} deve essere un URL HTTP(S) assoluto senza credenziali, fragment o query non ammesse`);
    }
    return value;
}
if (!FormatRegistry.Has("roofseye-rfc3339")) {
    FormatRegistry.Set("roofseye-rfc3339", isRfc3339Timestamp);
}
if (!FormatRegistry.Has("roofseye-serializable-http-url-v1")) {
    FormatRegistry.Set("roofseye-serializable-http-url-v1", isSerializableEnrichmentHttpUrl);
}
const rfc3339TimestampSchema = Type.String({ format: "roofseye-rfc3339" });
export const enrichmentHttpUrlSchema = Type.String({
    format: "roofseye-serializable-http-url-v1",
    maxLength: 2_000
});
const httpUrlSchema = enrichmentHttpUrlSchema;
const releaseKeySchema = Type.String({
    pattern: "^[A-Za-z0-9][A-Za-z0-9._:-]{0,199}$"
});
const uuidSchema = Type.String({
    pattern: "^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$"
});
const actorIdSchema = Type.String({ minLength: 1, maxLength: 200 });
export const enrichmentScopeKeySchema = Type.String({
    pattern: "^[a-z][a-z0-9_-]*:[a-zA-Z0-9._-]+$"
});
const scopeKeySchema = enrichmentScopeKeySchema;
const jsonValueSchema = Type.Recursive((self) => Type.Union([
    Type.Null(),
    Type.Boolean(),
    Type.Number(),
    Type.String(),
    Type.Array(self),
    Type.Object({}, { additionalProperties: self })
]));
const openRecordSchema = Type.Object({}, { additionalProperties: jsonValueSchema });
const nonEmptyRecordSchema = Type.Object({}, { additionalProperties: jsonValueSchema, minProperties: 1 });
export const enrichmentDomainKeySchema = Type.Union([
    Type.Literal("administrative"),
    Type.Literal("rer_dbtr"),
    Type.Literal("ade_cadastre"),
    Type.Literal("anncsu")
]);
export const enrichmentLayerKeySchema = Type.Union([
    Type.Literal("ISTAT_COMUNE"),
    Type.Literal("FAB"),
    Type.Literal("EDI"),
    Type.Literal("UVL"),
    Type.Literal("FDA"),
    Type.Literal("EDI_CT_USO"),
    Type.Literal("CADASTRAL_PARCEL"),
    Type.Literal("CADASTRAL_ZONING"),
    Type.Literal("ANNCSU_ADDRESS")
]);
export const enrichmentAcquisitionArtifactSchema = Type.Object({
    artifactKey: Type.String({ pattern: "^[A-Za-z0-9][A-Za-z0-9._:-]{0,199}$" }),
    sourceUrl: httpUrlSchema,
    originalName: Type.String({
        pattern: "^[^/\\\\\\u0000-\\u001F\\u007F]{1,500}$"
    }),
    mediaType: Type.String({
        pattern: "^[A-Za-z0-9!#$&^_.+-]+/[A-Za-z0-9!#$&^_.+-]+$",
        maxLength: 200
    }),
    byteSize: Type.Integer({ minimum: 1 }),
    sha256: sha256Schema,
    etag: Type.Union([Type.String({ pattern: "^[^\\r\\n]{1,500}$" }), Type.Null()]),
    lastModified: Type.Union([rfc3339TimestampSchema, Type.Null()]),
    storageKey: Type.String({
        pattern: "^sha256/[0-9a-f]{2}/[0-9a-f]{2}/[0-9a-f]{64}$"
    })
}, { additionalProperties: false });
export function assertEnrichmentAcquisitionArtifact(value) {
    if (!Value.Check(enrichmentAcquisitionArtifactSchema, value)) {
        throw new Error("Artefatto non conforme a enrichment-acquisition/v1");
    }
    const expectedStorageKey = `sha256/${value.sha256.slice(0, 2)}/${value.sha256.slice(2, 4)}/${value.sha256}`;
    if (value.storageKey !== expectedStorageKey) {
        throw new Error(`Storage key non coerente con SHA-256 per ${value.artifactKey}`);
    }
}
export const enrichmentAcquisitionManifestSchema = Type.Object({
    contractVersion: Type.Literal("enrichment-acquisition/v1"),
    sourceKey: Type.String({ minLength: 1, maxLength: 200 }),
    releaseKey: releaseKeySchema,
    scope: Type.Object({ regionCode: Type.String({ pattern: "^[0-9]{2}$" }) }, { additionalProperties: false }),
    publisher: Type.String({ minLength: 1, maxLength: 300 }),
    catalogUrl: httpUrlSchema,
    license: Type.Object({
        spdxLike: Type.String({ minLength: 1, maxLength: 100 }),
        reviewState: Type.Union([
            Type.Literal("pending"),
            Type.Literal("approved_publication"),
            Type.Literal("restricted"),
            Type.Literal("rejected")
        ])
    }, { additionalProperties: false }),
    publishedAt: Type.Union([rfc3339TimestampSchema, Type.Null()]),
    observedAt: Type.Union([rfc3339TimestampSchema, Type.Null()]),
    retrievedAt: Type.Union([rfc3339TimestampSchema, Type.Null()]),
    purpose: Type.Optional(Type.String({ minLength: 1, maxLength: 1_000 })),
    transformations: Type.Optional(Type.Array(Type.String({ minLength: 1, maxLength: 1_000 }))),
    artifacts: Type.Array(enrichmentAcquisitionArtifactSchema)
}, { $id: "EnrichmentAcquisitionManifestV1", additionalProperties: false });
export function assertEnrichmentAcquisitionReady(value) {
    if (!Value.Check(enrichmentAcquisitionManifestSchema, value)) {
        throw new Error("Manifest non conforme a enrichment-acquisition/v1");
    }
    if (value.observedAt === null || value.retrievedAt === null || value.artifacts.length === 0) {
        throw new Error("Manifest di acquisizione incompleto: date e artefatti sono obbligatori");
    }
    if (value.license.reviewState !== "approved_publication") {
        throw new Error("La licenza del manifest non è approvata per la pubblicazione");
    }
    if (Date.parse(value.retrievedAt) < Date.parse(value.observedAt)) {
        throw new Error("retrievedAt non può precedere observedAt");
    }
    if (value.publishedAt !== null && Date.parse(value.retrievedAt) < Date.parse(value.publishedAt)) {
        throw new Error("retrievedAt non può precedere publishedAt");
    }
    const artifactKeys = new Set();
    for (const artifact of value.artifacts) {
        assertEnrichmentAcquisitionArtifact(artifact);
        if (artifactKeys.has(artifact.artifactKey)) {
            throw new Error(`Artifact key duplicata: ${artifact.artifactKey}`);
        }
        artifactKeys.add(artifact.artifactKey);
    }
}
const administrativePreparedLinkageSchema = Type.Object({
    candidates: Type.Integer({ minimum: 0 }),
    linked: Type.Integer({ minimum: 0 }),
    unlinked: Type.Integer({ minimum: 0 })
}, { additionalProperties: false });
const administrativePreparedCountersSchema = Type.Object({
    input: Type.Integer({ minimum: 0 }),
    accepted: Type.Integer({ minimum: 0 }),
    updated: Type.Integer({ minimum: 0 }),
    rejected: Type.Integer({ minimum: 0 }),
    identicalDuplicates: Type.Integer({ minimum: 0 }),
    conflicts: Type.Integer({ minimum: 0 }),
    canonical: Type.Integer({ minimum: 0 }),
    linkage: administrativePreparedLinkageSchema
}, { additionalProperties: false });
const administrativePreparedTerritorySchema = Type.Object({ ISTAT_COMUNE: administrativePreparedCountersSchema }, { additionalProperties: false });
export const administrativePreparedManifestSchema = Type.Object({
    contractVersion: Type.Literal("administrative-prepared/v1"),
    releaseKey: releaseKeySchema,
    sourceKey: Type.Literal("istat.administrative-boundaries"),
    scope: Type.Object({ regionCode: Type.Literal("08") }, { additionalProperties: false }),
    mappingVersion: Type.Literal("istat-administrative/v1"),
    sourceManifestSha256: sha256Schema,
    sourceArtifacts: Type.Array(Type.Object({
        artifactKey: Type.String({ pattern: "^[A-Za-z0-9][A-Za-z0-9._:-]{0,199}$" }),
        sha256: sha256Schema,
        byteSize: Type.Integer({ minimum: 1 }),
        storageKey: Type.String({
            pattern: "^sha256/[0-9a-f]{2}/[0-9a-f]{2}/[0-9a-f]{64}$"
        }),
        sourceCrs: Type.Literal("EPSG:32632")
    }, { additionalProperties: false }), { minItems: 1 }),
    artifacts: Type.Tuple([
        Type.Object({
            artifactKey: Type.Literal("ISTAT_COMUNE"),
            file: Type.Literal("ISTAT_COMUNE.jsonl"),
            mediaType: Type.Literal("application/x-ndjson"),
            sha256: sha256Schema,
            byteSize: Type.Integer({ minimum: 0 }),
            recordCount: Type.Integer({ minimum: 0 }),
            crs: Type.Literal("EPSG:4326")
        }, { additionalProperties: false })
    ]),
    accounting: Type.Object({
        sourceFeatures: Type.Integer({ minimum: 0 }),
        outOfScope: Type.Integer({ minimum: 0 }),
        linkageApplicability: Type.Literal("not_applicable"),
        byLayer: Type.Object({ ISTAT_COMUNE: administrativePreparedCountersSchema }, { additionalProperties: false }),
        byTerritory: Type.Record(Type.String({ pattern: "^municipality:[0-9]{6}$" }), administrativePreparedTerritorySchema)
    }, { additionalProperties: false })
}, { $id: "AdministrativePreparedManifestV1", additionalProperties: false });
export function assertAdministrativePreparedManifest(value) {
    if (!Value.Check(administrativePreparedManifestSchema, value)) {
        throw new Error("Manifest non conforme a administrative-prepared/v1");
    }
    const sourceArtifactKeys = new Set();
    for (const artifact of value.sourceArtifacts) {
        if (sourceArtifactKeys.has(artifact.artifactKey)) {
            throw new Error(`Artifact sorgente amministrativo duplicato: ${artifact.artifactKey}`);
        }
        sourceArtifactKeys.add(artifact.artifactKey);
        const expectedStorageKey = `sha256/${artifact.sha256.slice(0, 2)}/${artifact.sha256.slice(2, 4)}/` + artifact.sha256;
        if (artifact.storageKey !== expectedStorageKey) {
            throw new Error(`Storage key sorgente non coerente per ${artifact.artifactKey}`);
        }
    }
    const layer = value.accounting.byLayer.ISTAT_COMUNE;
    assertAdministrativePreparedCounters(layer, "ISTAT_COMUNE");
    const territoryTotals = {
        input: 0,
        accepted: 0,
        updated: 0,
        rejected: 0,
        identicalDuplicates: 0,
        conflicts: 0,
        canonical: 0,
        linkage: { candidates: 0, linked: 0, unlinked: 0 }
    };
    for (const [territoryKey, territory] of Object.entries(value.accounting.byTerritory)) {
        assertAdministrativePreparedCounters(territory.ISTAT_COMUNE, territoryKey);
        addAdministrativePreparedCounters(territoryTotals, territory.ISTAT_COMUNE);
    }
    if (!administrativePreparedCountersEqual(territoryTotals, layer)) {
        throw new Error("I conteggi territoriali amministrativi non riconciliano il layer");
    }
    if (value.accounting.sourceFeatures !== layer.input + value.accounting.outOfScope) {
        throw new Error("I conteggi sorgente amministrativi non riconciliano lo scope");
    }
    if (value.artifacts[0].recordCount !== layer.input - layer.rejected) {
        throw new Error("Il recordCount amministrativo non riconcilia i record preparati");
    }
}
function administrativePreparedCountersEqual(left, right) {
    return (left.input === right.input &&
        left.accepted === right.accepted &&
        left.updated === right.updated &&
        left.rejected === right.rejected &&
        left.identicalDuplicates === right.identicalDuplicates &&
        left.conflicts === right.conflicts &&
        left.canonical === right.canonical &&
        left.linkage.candidates === right.linkage.candidates &&
        left.linkage.linked === right.linkage.linked &&
        left.linkage.unlinked === right.linkage.unlinked);
}
export function canonicalizeAdministrativePreparedManifest(value) {
    assertAdministrativePreparedManifest(value);
    return canonicalizeJson(value);
}
function assertAdministrativePreparedCounters(counters, key) {
    if (counters.input !==
        counters.accepted + counters.rejected + counters.identicalDuplicates + counters.conflicts ||
        counters.canonical !== counters.accepted ||
        counters.updated !== 0 ||
        counters.linkage.candidates !== 0 ||
        counters.linkage.linked !== 0 ||
        counters.linkage.unlinked !== 0) {
        throw new Error(`Conteggi amministrativi non riconciliati per ${key}`);
    }
}
function addAdministrativePreparedCounters(target, source) {
    target.input += source.input;
    target.accepted += source.accepted;
    target.updated += source.updated;
    target.rejected += source.rejected;
    target.identicalDuplicates += source.identicalDuplicates;
    target.conflicts += source.conflicts;
    target.canonical += source.canonical;
    target.linkage.candidates += source.linkage.candidates;
    target.linkage.linked += source.linkage.linked;
    target.linkage.unlinked += source.linkage.unlinked;
}
const geoJsonPositionSchema = Type.Array(Type.Number(), { minItems: 2, maxItems: 3 });
const geoJsonLinearRingSchema = Type.Array(geoJsonPositionSchema, { minItems: 4 });
const geoJsonPolygonCoordinatesSchema = Type.Array(geoJsonLinearRingSchema, { minItems: 1 });
export const enrichmentPreparedGeometrySchema = Type.Union([
    Type.Object({
        type: Type.Literal("Point"),
        coordinates: geoJsonPositionSchema
    }, { additionalProperties: false }),
    Type.Object({
        type: Type.Literal("Polygon"),
        coordinates: geoJsonPolygonCoordinatesSchema
    }, { additionalProperties: false }),
    Type.Object({
        type: Type.Literal("MultiPolygon"),
        coordinates: Type.Array(geoJsonPolygonCoordinatesSchema, { minItems: 1 })
    }, { additionalProperties: false })
]);
const preparedRecordProperties = {
    contractVersion: Type.Literal("enrichment-record/v1"),
    releaseKey: releaseKeySchema,
    artifactKey: Type.String({ minLength: 1, maxLength: 200 }),
    externalId: Type.String({ minLength: 1, maxLength: 500 }),
    occurrenceIndex: Type.Integer({ minimum: 0 }),
    sourceRecordOffset: nonEmptyRecordSchema,
    sourceCrs: Type.String({ minLength: 1, maxLength: 100 }),
    sourceProperties: openRecordSchema,
    normalized: openRecordSchema,
    canonicalContentHash: sha256Schema,
    payloadHash: sha256Schema
};
const spatialPreparedRecordSchema = Type.Object({
    ...preparedRecordProperties,
    layerKey: Type.Union([
        Type.Literal("ISTAT_COMUNE"),
        Type.Literal("FAB"),
        Type.Literal("EDI"),
        Type.Literal("UVL"),
        Type.Literal("FDA"),
        Type.Literal("CADASTRAL_PARCEL"),
        Type.Literal("CADASTRAL_ZONING")
    ]),
    geometry: enrichmentPreparedGeometrySchema
}, { additionalProperties: false });
const nullableGeometryPreparedRecordSchema = Type.Object({
    ...preparedRecordProperties,
    layerKey: Type.Union([Type.Literal("EDI_CT_USO"), Type.Literal("ANNCSU_ADDRESS")]),
    geometry: Type.Union([enrichmentPreparedGeometrySchema, Type.Null()])
}, { additionalProperties: false });
export const enrichmentPreparedRecordSchema = Type.Union([spatialPreparedRecordSchema, nullableGeometryPreparedRecordSchema], { $id: "EnrichmentPreparedRecordV1" });
export function assertEnrichmentPreparedRecord(value) {
    if (!Value.Check(enrichmentPreparedRecordSchema, value)) {
        throw new Error("Record preparato non conforme a enrichment-record/v1");
    }
    if (value.geometry !== null)
        assertClosedGeoJsonRings(value.geometry);
}
function assertClosedGeoJsonRings(geometry) {
    if (geometry.type === "Point")
        return;
    const polygons = geometry.type === "Polygon" ? [geometry.coordinates] : geometry.coordinates;
    for (const polygon of polygons) {
        for (const ring of polygon) {
            const first = ring[0];
            const last = ring.at(-1);
            if (first === undefined ||
                last === undefined ||
                first.length !== last.length ||
                first.some((coordinate, index) => coordinate !== last[index])) {
                throw new Error("Gli anelli GeoJSON Polygon devono essere chiusi");
            }
            const distinctPositions = new Set(ring.map((position) => JSON.stringify(position.slice(0, 2))));
            if (distinctPositions.size < 3) {
                throw new Error("Gli anelli GeoJSON Polygon devono avere almeno tre posizioni distinte");
            }
        }
    }
}
const validationGateSchema = Type.Object({
    key: Type.String({ minLength: 1, maxLength: 100 }),
    blocking: Type.Boolean(),
    state: Type.Union([Type.Literal("pass"), Type.Literal("fail")]),
    observed: Type.Number(),
    limit: Type.Number()
}, { additionalProperties: false });
const validationLayerCountsSchema = Type.Object({
    input: Type.Integer({ minimum: 0 }),
    accepted: Type.Integer({ minimum: 0 }),
    rejected: Type.Integer({ minimum: 0 }),
    identicalDuplicates: Type.Integer({ minimum: 0 }),
    conflicts: Type.Integer({ minimum: 0 }),
    canonical: Type.Integer({ minimum: 0 })
}, { additionalProperties: false });
const enrichmentValidationLayersSchema = Type.Object({
    ISTAT_COMUNE: Type.Optional(validationLayerCountsSchema),
    FAB: Type.Optional(validationLayerCountsSchema),
    EDI: Type.Optional(validationLayerCountsSchema),
    UVL: Type.Optional(validationLayerCountsSchema),
    FDA: Type.Optional(validationLayerCountsSchema),
    EDI_CT_USO: Type.Optional(validationLayerCountsSchema),
    CADASTRAL_PARCEL: Type.Optional(validationLayerCountsSchema),
    CADASTRAL_ZONING: Type.Optional(validationLayerCountsSchema),
    ANNCSU_ADDRESS: Type.Optional(validationLayerCountsSchema)
}, { additionalProperties: false, minProperties: 1 });
const enrichmentValidationDependenciesSchema = Type.Object({
    administrative: Type.Object({
        domainKey: Type.Literal("administrative"),
        scopeKey: enrichmentScopeKeySchema,
        releaseId: uuidSchema,
        releaseKey: releaseKeySchema,
        activationVersion: Type.Integer({ minimum: 1 }),
        validationReportSha256: sha256Schema,
        assignmentVersion: Type.String({ minLength: 1, maxLength: 200 })
    }, { additionalProperties: false })
}, { additionalProperties: false });
export const enrichmentValidationReportSchema = Type.Object({
    contractVersion: Type.Literal("enrichment-validation/v1"),
    releaseKey: releaseKeySchema,
    mappingVersion: Type.String({ minLength: 1, maxLength: 200 }),
    generatedAt: rfc3339TimestampSchema,
    decision: Type.Union([Type.Literal("pass"), Type.Literal("fail")]),
    gates: Type.Array(validationGateSchema, { minItems: 1 }),
    layers: enrichmentValidationLayersSchema,
    dependencies: Type.Optional(enrichmentValidationDependenciesSchema),
    territories: openRecordSchema,
    differencesFromActive: openRecordSchema,
    reportSha256: sha256Schema
}, { $id: "EnrichmentValidationReportV1", additionalProperties: false });
export function assertEnrichmentValidationReport(value) {
    if (!Value.Check(enrichmentValidationReportSchema, value)) {
        throw new Error("Validation report non conforme a enrichment-validation/v1");
    }
    for (const [layerKey, counts] of Object.entries(value.layers)) {
        const reconciled = counts.accepted + counts.rejected + counts.identicalDuplicates + counts.conflicts;
        if (counts.input !== reconciled || counts.canonical !== counts.accepted) {
            throw new Error(`Conteggi non riconciliati per il layer ${layerKey}`);
        }
    }
    const hasBlockingFailure = value.gates.some((gate) => gate.blocking && gate.state === "fail");
    const hasConflict = Object.values(value.layers).some((counts) => counts.conflicts > 0);
    const rejected = Object.values(value.layers).reduce((total, counts) => total + counts.rejected, 0);
    if (value.decision === "pass" && (hasBlockingFailure || hasConflict)) {
        throw new Error("La decisione pass non è coerente con gate o conflitti bloccanti");
    }
    if (value.decision === "pass" && rejected > 0) {
        const rejectionGates = value.gates.filter((gate) => gate.key === "rejections");
        const policyGates = value.gates.filter((gate) => gate.key === "rejection_policy_match");
        const rejectionGate = rejectionGates[0];
        const policyGate = policyGates[0];
        if (rejectionGates.length !== 1 ||
            policyGates.length !== 1 ||
            !rejectionGate?.blocking ||
            rejectionGate.state !== "pass" ||
            rejectionGate.observed !== rejected ||
            rejectionGate.limit !== rejected ||
            !policyGate?.blocking ||
            policyGate.state !== "pass" ||
            policyGate.observed !== 0 ||
            policyGate.limit !== 0) {
            throw new Error("La decisione pass con rifiuti richiede una policy di esclusione esatta e bloccante");
        }
    }
    const requiresMultipartRepairGate = value.mappingVersion === "ade-cadastre/v3" ||
        value.mappingVersion === ADE_CADASTRE_MAPPING_VERSION;
    if (value.decision === "pass" && requiresMultipartRepairGate) {
        const multipartPolicyGates = value.gates.filter((gate) => gate.key === "multipart_repair_policy_match");
        const multipartPolicyGate = multipartPolicyGates[0];
        if (multipartPolicyGates.length !== 1 ||
            !multipartPolicyGate?.blocking ||
            multipartPolicyGate.state !== "pass" ||
            multipartPolicyGate.observed !== 0 ||
            multipartPolicyGate.limit !== 0) {
            throw new Error("La decisione pass AdE v3/v4 richiede il gate bloccante multipart_repair_policy_match");
        }
    }
    if (value.decision === "pass" && value.mappingVersion === ADE_CADASTRE_MAPPING_VERSION) {
        const normalizationPolicyGates = value.gates.filter((gate) => gate.key === "geometry_normalization_policy_match");
        const normalizationPolicyGate = normalizationPolicyGates[0];
        if (normalizationPolicyGates.length !== 1 ||
            !normalizationPolicyGate?.blocking ||
            normalizationPolicyGate.state !== "pass" ||
            normalizationPolicyGate.observed !== 0 ||
            normalizationPolicyGate.limit !== 0) {
            throw new Error("La decisione pass AdE v4 richiede il gate bloccante geometry_normalization_policy_match");
        }
    }
    if (value.decision === "fail" && !hasBlockingFailure && !hasConflict) {
        throw new Error("La decisione fail richiede un gate bloccante fallito o un conflitto");
    }
}
export function canonicalizeEnrichmentValidationReport(value) {
    assertEnrichmentValidationReport(value);
    const unsignedReport = Object.fromEntries(Object.entries(value).filter(([key]) => key !== "reportSha256"));
    return canonicalizeJson(unsignedReport);
}
function canonicalizeJson(value) {
    if (value === null || typeof value === "boolean" || typeof value === "string") {
        return JSON.stringify(value);
    }
    if (typeof value === "number") {
        if (!Number.isFinite(value))
            throw new Error("Il JSON canonico richiede numeri finiti");
        return JSON.stringify(value);
    }
    if (Array.isArray(value)) {
        return `[${value.map((entry) => canonicalizeJson(entry)).join(",")}]`;
    }
    if (typeof value === "object") {
        return `{${Object.keys(value)
            .sort()
            .map((key) => `${JSON.stringify(key)}:${canonicalizeJson(value[key])}`)
            .join(",")}}`;
    }
    throw new Error("Valore non serializzabile nel JSON canonico");
}
export function assertEnrichmentScopeKey(value) {
    if (!Value.Check(enrichmentScopeKeySchema, value)) {
        throw new Error("Scope enrichment non valido");
    }
}
const acquireCommandSchema = Type.Object({
    command: Type.Literal("acquire"),
    sourceKey: Type.String({ minLength: 1 }),
    scopeKey: scopeKeySchema,
    output: Type.String({ minLength: 1 })
}, { additionalProperties: false });
const prepareCommandSchema = Type.Object({
    command: Type.Literal("prepare"),
    manifestPath: Type.String({ minLength: 1 }),
    output: Type.String({ minLength: 1 })
}, { additionalProperties: false });
const ingestCommandSchema = Type.Object({
    command: Type.Literal("ingest"),
    manifestPath: Type.String({ minLength: 1 }),
    resume: Type.Optional(Type.Boolean())
}, { additionalProperties: false });
const validateCommandSchema = Type.Object({
    command: Type.Literal("validate"),
    manifestPath: Type.String({ minLength: 1 }),
    releaseKey: releaseKeySchema,
    output: Type.String({ minLength: 1 })
}, { additionalProperties: false });
const approveCommandSchema = Type.Object({
    command: Type.Literal("approve"),
    releaseKey: releaseKeySchema,
    reportSha256: sha256Schema,
    actorId: actorIdSchema
}, { additionalProperties: false });
const activateCommandSchema = Type.Union([
    Type.Object({
        command: Type.Literal("activate"),
        releaseKey: releaseKeySchema,
        expectedCurrentReleaseKey: Type.Null(),
        expectedActivationVersion: Type.Null(),
        actorId: actorIdSchema
    }, { additionalProperties: false }),
    Type.Object({
        command: Type.Literal("activate"),
        releaseKey: releaseKeySchema,
        expectedCurrentReleaseKey: releaseKeySchema,
        expectedActivationVersion: Type.Integer({ minimum: 1 }),
        actorId: actorIdSchema
    }, { additionalProperties: false })
]);
const rollbackCommandSchema = Type.Object({
    command: Type.Literal("rollback"),
    domainKey: enrichmentDomainKeySchema,
    scopeKey: scopeKeySchema,
    toReleaseKey: releaseKeySchema,
    actorId: actorIdSchema
}, { additionalProperties: false });
const statusCommandSchema = Type.Object({
    command: Type.Literal("status"),
    releaseKey: releaseKeySchema,
    json: Type.Optional(Type.Boolean())
}, { additionalProperties: false });
const deriveCommandSchema = Type.Object({
    command: Type.Literal("derive"),
    kind: Type.Literal("building-cadastral-links"),
    rerDbtrReleaseKey: releaseKeySchema,
    adeCadastreReleaseKey: releaseKeySchema,
    mappingVersion: Type.Literal("building-cadastre/v1"),
    municipalityCodes: Type.Optional(Type.Array(Type.String({ pattern: "^[0-9]{6}$" }), {
        minItems: 1,
        uniqueItems: true
    }))
}, { additionalProperties: false });
export const enrichmentCommandSchema = Type.Union([
    acquireCommandSchema,
    prepareCommandSchema,
    ingestCommandSchema,
    validateCommandSchema,
    approveCommandSchema,
    activateCommandSchema,
    rollbackCommandSchema,
    statusCommandSchema,
    deriveCommandSchema
]);
export const enrichmentActivationSchema = Type.Object({
    contractVersion: Type.Literal("enrichment-activation/v1"),
    domainKey: enrichmentDomainKeySchema,
    scopeKey: scopeKeySchema,
    releaseKey: releaseKeySchema,
    previousReleaseKey: Type.Union([releaseKeySchema, Type.Null()]),
    activationVersion: Type.Integer({ minimum: 1 }),
    actorId: actorIdSchema,
    activatedAt: rfc3339TimestampSchema
}, { $id: "EnrichmentActivationV1", additionalProperties: false });
export function assertEnrichmentActivation(value) {
    if (!Value.Check(enrichmentActivationSchema, value)) {
        throw new Error("Attivazione non conforme a enrichment-activation/v1");
    }
    if (value.previousReleaseKey === value.releaseKey) {
        throw new Error("La release precedente deve essere diversa dalla release attivata");
    }
}
export const enrichmentErrorCodes = [
    "ARTIFACT_HASH_MISMATCH",
    "SOURCE_SCHEMA_DRIFT",
    "IDENTITY_CONFLICT",
    "COUNT_RECONCILIATION_FAILED",
    "GEOMETRY_REJECTED",
    "LINEAGE_ORPHAN",
    "PRIVACY_FIELD_DETECTED",
    "VALIDATION_REPORT_STALE",
    "ACTIVATION_CONFLICT",
    "PROJECTION_SWAP_FAILED",
    "VERIFICATION_UNRESOLVED",
    "PROVIDER_RESULT_CONFLICT",
    "SMOKE_PAID_CALL_FORBIDDEN",
    "SMOKE_SESSION_EXPIRED",
    "SMOKE_ARTIFACT_UNSAFE"
];
export const enrichmentErrorCodeSchema = Type.Union([
    Type.Literal("ARTIFACT_HASH_MISMATCH"),
    Type.Literal("SOURCE_SCHEMA_DRIFT"),
    Type.Literal("IDENTITY_CONFLICT"),
    Type.Literal("COUNT_RECONCILIATION_FAILED"),
    Type.Literal("GEOMETRY_REJECTED"),
    Type.Literal("LINEAGE_ORPHAN"),
    Type.Literal("PRIVACY_FIELD_DETECTED"),
    Type.Literal("VALIDATION_REPORT_STALE"),
    Type.Literal("ACTIVATION_CONFLICT"),
    Type.Literal("PROJECTION_SWAP_FAILED"),
    Type.Literal("VERIFICATION_UNRESOLVED"),
    Type.Literal("PROVIDER_RESULT_CONFLICT"),
    Type.Literal("SMOKE_PAID_CALL_FORBIDDEN"),
    Type.Literal("SMOKE_SESSION_EXPIRED"),
    Type.Literal("SMOKE_ARTIFACT_UNSAFE")
]);
const errorMessageSchema = Type.String({ minLength: 1, maxLength: 1_000 });
const diagnosticTokenSchema = Type.String({
    pattern: "^[A-Za-z0-9][A-Za-z0-9._:/-]{0,499}$"
});
const diagnosticStorageKeySchema = Type.String({
    pattern: "^sha256/[0-9a-f]{2}/[0-9a-f]{2}/[0-9a-f]{64}$"
});
export const enrichmentErrorDetailsSchema = Type.Object({
    artifactKey: Type.Optional(diagnosticTokenSchema),
    releaseKey: Type.Optional(releaseKeySchema),
    expectedReleaseKey: Type.Optional(Type.Union([releaseKeySchema, Type.Null()])),
    actualReleaseKey: Type.Optional(Type.Union([releaseKeySchema, Type.Null()])),
    domainKey: Type.Optional(enrichmentDomainKeySchema),
    scopeKey: Type.Optional(scopeKeySchema),
    layerKey: Type.Optional(enrichmentLayerKeySchema),
    storageKey: Type.Optional(diagnosticStorageKeySchema),
    expectedSha256: Type.Optional(sha256Schema),
    actualSha256: Type.Optional(sha256Schema),
    identityHash: Type.Optional(sha256Schema),
    expectedByteSize: Type.Optional(Type.Integer({ minimum: 0 })),
    actualByteSize: Type.Optional(Type.Integer({ minimum: 0 })),
    attemptNumber: Type.Optional(Type.Integer({ minimum: 1 })),
    retryCount: Type.Optional(Type.Integer({ minimum: 0 })),
    policyVersion: Type.Optional(Type.Integer({ minimum: 1 })),
    requestedCents: Type.Optional(Type.Integer({ minimum: 0 })),
    remainingCents: Type.Optional(Type.Integer({ minimum: 0 })),
    occurrenceCount: Type.Optional(Type.Integer({ minimum: 0 })),
    contentHashCount: Type.Optional(Type.Integer({ minimum: 0 })),
    observed: Type.Optional(Type.Number()),
    limit: Type.Optional(Type.Number()),
    gateKey: Type.Optional(diagnosticTokenSchema),
    fieldName: Type.Optional(diagnosticTokenSchema),
    reasonCode: Type.Optional(diagnosticTokenSchema),
    expectedState: Type.Optional(diagnosticTokenSchema),
    actualState: Type.Optional(diagnosticTokenSchema),
    expectedSchemaVersion: Type.Optional(diagnosticTokenSchema),
    actualSchemaVersion: Type.Optional(diagnosticTokenSchema),
    ruleVersion: Type.Optional(diagnosticTokenSchema),
    runId: Type.Optional(diagnosticTokenSchema),
    counts: Type.Optional(validationLayerCountsSchema)
}, { additionalProperties: false, minProperties: 1 });
function errorVariant(codes, retry) {
    const code = codes.length === 1
        ? Type.Literal(codes[0])
        : Type.Union(codes.map((value) => Type.Literal(value)));
    return Type.Object({
        contractVersion: Type.Literal("enrichment-error/v1"),
        code,
        message: errorMessageSchema,
        retry: Type.Literal(retry),
        details: Type.Optional(enrichmentErrorDetailsSchema)
    }, { additionalProperties: false });
}
export const enrichmentErrorSchema = Type.Union([
    errorVariant(["ARTIFACT_HASH_MISMATCH"], "reacquire_manual"),
    errorVariant(["SOURCE_SCHEMA_DRIFT", "IDENTITY_CONFLICT", "PROVIDER_RESULT_CONFLICT"], "manual_review"),
    errorVariant(["COUNT_RECONCILIATION_FAILED", "LINEAGE_ORPHAN"], "fix_retry"),
    errorVariant(["GEOMETRY_REJECTED"], "record_manual"),
    errorVariant(["PRIVACY_FIELD_DETECTED", "SMOKE_PAID_CALL_FORBIDDEN"], "hard_stop"),
    errorVariant(["VALIDATION_REPORT_STALE"], "revalidate"),
    errorVariant(["ACTIVATION_CONFLICT"], "reload_review"),
    errorVariant(["PROJECTION_SWAP_FAILED"], "retry_active_unchanged"),
    errorVariant(["VERIFICATION_UNRESOLVED"], "retry_on_new_evidence"),
    errorVariant(["SMOKE_SESSION_EXPIRED"], "reauthenticate"),
    errorVariant(["SMOKE_ARTIFACT_UNSAFE"], "sanitize_hard_stop")
], { $id: "EnrichmentErrorV1" });
export function assertEnrichmentError(value) {
    if (!Value.Check(enrichmentErrorSchema, value)) {
        throw new Error("Errore non conforme a enrichment-error/v1");
    }
}
const smokeArtifactReferenceSchema = Type.Object({
    kind: Type.String({ minLength: 1, maxLength: 100 }),
    storageKey: Type.String({
        pattern: "^(?![a-zA-Z][a-zA-Z0-9+.-]*:)(?!/)(?!.*(?:^|/)\\.\\.(?:/|$))(?!.*[?#\\\\])[A-Za-z0-9][A-Za-z0-9._/-]{0,999}$"
    })
}, { additionalProperties: false });
const smokeProviderSchema = Type.Union([
    Type.Object({
        mode: Type.Union([Type.Literal("mock"), Type.Literal("sandbox")]),
        policyVersion: Type.Null(),
        incrementalCostCents: Type.Literal(0)
    }, { additionalProperties: false }),
    Type.Object({
        mode: Type.Literal("paid"),
        policyVersion: Type.Integer({ minimum: 1 }),
        incrementalCostCents: Type.Integer({ minimum: 0 })
    }, { additionalProperties: false })
]);
const smokeStepSchema = Type.Object({
    key: Type.String({ minLength: 1, maxLength: 200 }),
    state: Type.Union([Type.Literal("pass"), Type.Literal("fail"), Type.Literal("blocked")]),
    durationMs: Type.Integer({ minimum: 0 })
}, { additionalProperties: false });
const smokePassedStepSchema = Type.Object({
    key: Type.String({ minLength: 1, maxLength: 200 }),
    state: Type.Literal("pass"),
    durationMs: Type.Integer({ minimum: 0 })
}, { additionalProperties: false });
const smokeDownloadProperties = {
    kind: Type.String({ minLength: 1, maxLength: 100 }),
    mime: Type.Literal("application/pdf"),
    byteSize: Type.Integer({ minimum: 1 }),
    sha256: sha256Schema
};
const smokeDownloadSchema = Type.Object({ ...smokeDownloadProperties, expectedText: Type.Boolean() }, { additionalProperties: false });
const smokePassedDownloadSchema = Type.Object({ ...smokeDownloadProperties, expectedText: Type.Literal(true) }, { additionalProperties: false });
const smokeDiagnosticsSchema = Type.Object({
    consoleErrors: Type.Integer({ minimum: 0 }),
    failedRequests: Type.Integer({ minimum: 0 }),
    a11yViolations: Type.Integer({ minimum: 0 })
}, { additionalProperties: false });
const smokePassedDiagnosticsSchema = Type.Object({
    consoleErrors: Type.Literal(0),
    failedRequests: Type.Literal(0),
    a11yViolations: Type.Literal(0)
}, { additionalProperties: false });
const smokeReportProperties = {
    contractVersion: Type.Literal("smoke-run/v1"),
    runId: Type.String({
        pattern: "^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$"
    }),
    scenarioVersion: Type.String({ minLength: 1, maxLength: 200 }),
    target: Type.Union([Type.Literal("local"), Type.Literal("published")]),
    commitSha: Type.String({ pattern: "^[0-9a-f]{7,64}$" }),
    deployId: Type.Union([Type.String({ minLength: 1, maxLength: 300 }), Type.Null()]),
    browserVersion: Type.String({ minLength: 1, maxLength: 300 }),
    viewport: Type.Object({
        name: Type.String({ minLength: 1, maxLength: 100 }),
        width: Type.Integer({ minimum: 1 }),
        height: Type.Integer({ minimum: 1 })
    }, { additionalProperties: false }),
    accountSubjectHash: sha256Schema,
    provider: smokeProviderSchema,
    diagnostics: smokeDiagnosticsSchema,
    screenshots: Type.Optional(Type.Array(smokeArtifactReferenceSchema)),
    videos: Type.Optional(Type.Array(smokeArtifactReferenceSchema)),
    startedAt: rfc3339TimestampSchema,
    completedAt: rfc3339TimestampSchema
};
export const enrichmentSmokeReportSchema = Type.Union([
    Type.Object({
        ...smokeReportProperties,
        diagnostics: smokePassedDiagnosticsSchema,
        steps: Type.Array(smokePassedStepSchema, { minItems: 1 }),
        downloads: Type.Array(smokePassedDownloadSchema, { minItems: 1 }),
        result: Type.Literal("pass")
    }, { additionalProperties: false }),
    Type.Object({
        ...smokeReportProperties,
        steps: Type.Array(smokeStepSchema, { minItems: 1 }),
        downloads: Type.Array(smokeDownloadSchema),
        result: Type.Union([Type.Literal("fail"), Type.Literal("blocked")])
    }, { additionalProperties: false })
], { $id: "EnrichmentSmokeReportV1" });
export function assertEnrichmentSmokeReport(value) {
    if (!Value.Check(enrichmentSmokeReportSchema, value)) {
        throw new Error("Report Smoke non conforme a smoke-run/v1");
    }
    if (Date.parse(value.completedAt) < Date.parse(value.startedAt)) {
        throw new Error("Il report Smoke termina prima del suo avvio");
    }
    if (value.target === "published" && value.deployId === null) {
        throw new Error("Una run pubblicata deve dichiarare il deploy verificato");
    }
    if (value.target === "local" && value.deployId !== null) {
        throw new Error("Una run locale non deve dichiarare un deploy pubblicato");
    }
    if (value.result === "fail" && !value.steps.some((step) => step.state === "fail")) {
        throw new Error("Una run fallita deve indicare almeno uno step fallito");
    }
    if (value.result === "blocked" && !value.steps.some((step) => step.state === "blocked")) {
        throw new Error("Una run bloccata deve indicare almeno uno step bloccato");
    }
}
//# sourceMappingURL=enrichment.js.map